import {
	AuditFieldValue,
	formatFieldName,
	formatValue,
	readAuditString,
	shouldShowField,
} from '@dealstack/frontend/shared/components/Audit/AuditLayout/utils.ts';
import { TransformedAuditData } from '@dealstack/frontend/shared/components/Audit/types.ts';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns.tsx';
import { bankAccountIdentifierLabel } from '@dealstack/shared/lib/common/business/bankAccountIdentifiers';
import {
	AuditAction,
	AuditTct,
	LoanDefinitionType,
	SecurityType,
} from '@dealstack/shared/lib/generated';

interface SelectOption {
	value: string;
	label: string;
}

interface SecurityOption extends SelectOption {
	unitDenominator?: number;
}

export interface AuditTableDataContext {
	tCapManage: (key: string, options?: { defaultValue: string }) => string;
	tSecurityTypeTranslation?: (securityType?: SecurityType | LoanDefinitionType) => string;
	personsSelectOptions?: SelectOption[];
	mipSelectOptions?: SelectOption[];
	securitiesSelectOptions?: SecurityOption[];
	bankAccountFormatRulesSelectOptions?: SelectOption[];
}

const TRANSACTION_FIELD_ORDER = [
	'date',
	'securityId',
	'type',
	'id',
	'destinationId',
	'toMipId',
	'sourceId',
	'fromMipId',
	'units',
	'cost',
];

const SECURITY_FIELD_ORDER = [
	'name',
	'type',
	'currency',
	'issuerId',
	'issuedIn',
	'nominalValue',
	'votesPerUnit',
	'fairMarketValuePerUnit',
	'equityBasis',
	'residueRights',
	'unitDenominator',
];

const PERSON_FIELD_ORDER = [
	'displayName',
	'firstName',
	'lastName',
	'email',
	'phoneNumber',
	'telephonePrefix',
	'dateOfBirth',
	'gender',
	'placeOfBirth',
	'countryOfBirth',
	'citizenship',
	'domicile',
	'type',
	'providerDetailsFriendlyName',
	'providerDetailsLegalForm',
];

const BANK_ACCOUNT_FIELD_ORDER = [
	'status',
	'bankName',
	'accountHolder',
	'iban',
	'swiftCode',
	'accountNumber',
	'country',
	'countryCode',
	'currency',
	'investorInstructions',
];

const TAX_RESIDENCE_FIELD_ORDER = ['countryCode', 'authority', 'number'];

const ADDRESS_FIELD_ORDER = [
	'singleAddressString',
	'street',
	'houseNumber',
	'postCode',
	'city',
	'country',
];

const PERSON_SCOPE_FIELD_ORDER = ['isManagementEntity', 'canIssueSecurities'];

const PERSON_RELATION_FIELD_ORDER = ['relationType', 'sourcePersonId', 'targetPersonId'];

const INVESTMENT_WORKFLOW_FIELD_ORDER = [
	'type',
	'status',
	'paymentStatus',
	'isCompleted',
	'completionStep',
	'paymentAnnotation',
	'targetStartDate',
	'targetCompletionDate',
	'startedAt',
	'approvedAt',
];

const SHARE_ASSOCIATION_FIELD_ORDER = [
	'securityName',
	'number',
	'cost',
	'transactionType',
	'expectedTransactionType',
	'vestingStartDate',
	'isNegative',
];

const LOAN_FIELD_ORDER = ['loanDefinitionName', 'amount', 'amountRemaining'];

const FIELD_ORDER_BY_TABLE: Record<string, string[]> = {
	Security: SECURITY_FIELD_ORDER,
	Person: PERSON_FIELD_ORDER,
	BankAccount: BANK_ACCOUNT_FIELD_ORDER,
	TaxResidence: TAX_RESIDENCE_FIELD_ORDER,
	Address: ADDRESS_FIELD_ORDER,
	PersonScope: PERSON_SCOPE_FIELD_ORDER,
	PersonPerson: PERSON_RELATION_FIELD_ORDER,
	InvestmentWorkflow: INVESTMENT_WORKFLOW_FIELD_ORDER,
	ShareAssociation: SHARE_ASSOCIATION_FIELD_ORDER,
	Loan: LOAN_FIELD_ORDER,
};

const sortFieldsByOrder = (
	data: TransformedAuditData[],
	tableName?: string
): TransformedAuditData[] => {
	const baseOrder = FIELD_ORDER_BY_TABLE[tableName ?? ''] ?? TRANSACTION_FIELD_ORDER;
	const getFieldOrder = (fieldId: string): number => {
		const index = baseOrder.indexOf(fieldId);
		return index === -1 ? baseOrder.length : index;
	};

	return data.sort((a, b) => getFieldOrder(a.id) - getFieldOrder(b.id));
};

// Object.entries over an audit payload yields unknown. Non-scalars (a citizenship array,
// say) stringify exactly as formatValue's own toString fallback would.
export const toAuditFieldValue = (value: unknown): AuditFieldValue => {
	if (value === null || value === undefined || value instanceof Date) {
		return value;
	}
	if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
		return value;
	}

	return String(value);
};

const ENTRY_FORMAT_FIELD = 'bankAccountFormatRuleId';

/**
 * Which entry format the identifier belongs to, as its name. A backfill can rewire this
 * without touching the value (MIP-2543 linked every IBAN to its country's format), so it
 * needs a row of its own — otherwise the entry shows the same value on both sides and
 * reads as a change to the IBAN that never happened.
 *
 * Falls back to the raw id: the catalogue is fetched per country, so a rule from a country
 * the account has since moved away from will not be in it.
 */
const toEntryFormatRows = (
	audit: AuditTct,
	context: AuditTableDataContext
): TransformedAuditData[] => {
	const before = readAuditString(audit.oldData, ENTRY_FORMAT_FIELD);
	const after = readAuditString(audit.newData, ENTRY_FORMAT_FIELD);

	if (before === after) {
		return [];
	}

	const toLabel = (ruleId?: string) =>
		context.bankAccountFormatRulesSelectOptions?.find((option) => option.value === ruleId)
			?.label ??
		ruleId ??
		LONG_DASH;

	return [
		{
			id: ENTRY_FORMAT_FIELD,
			field: formatFieldName(ENTRY_FORMAT_FIELD, context.tCapManage, 'BankAccountIdentifier'),
			beforeValue: toLabel(before),
			afterValue: toLabel(after),
			isChanged: true,
			type: 'update',
		},
	];
};

/**
 * An identifier row is a key/value pair, so the generic diff would report "Field: iban"
 * and "Value: … → …" as two rows. One row named after the identifier says the same thing,
 * followed by its entry format when that moved.
 */
const transformIdentifierAudit = (
	audit: AuditTct,
	context: AuditTableDataContext
): TransformedAuditData[] => {
	const key =
		readAuditString(audit.newData, 'key') ?? readAuditString(audit.oldData, 'key') ?? '';
	const field = bankAccountIdentifierLabel(key);
	const beforeValue = readAuditString(audit.oldData, 'value') ?? '';
	const afterValue = readAuditString(audit.newData, 'value') ?? '';

	if (audit.action === AuditAction.CREATE) {
		return [{ id: key, field, value: afterValue, type: 'created' }];
	}
	if (audit.action === AuditAction.DELETE) {
		return [{ id: key, field, value: beforeValue, type: 'deleted' }];
	}

	return [
		{
			id: key,
			field,
			beforeValue,
			afterValue,
			isChanged: beforeValue !== afterValue,
			type: 'update',
		},
		...toEntryFormatRows(audit, context),
	];
};

/**
 * The rows the audit table renders for one audit entry: the changed fields of an update,
 * or the recorded fields of a create/delete. Fields outside the table's allow-list are
 * dropped, so internal columns never reach a reviewer.
 */
export const transformAuditToTableData = (
	audit: AuditTct | null,
	context: AuditTableDataContext
): TransformedAuditData[] => {
	if (!audit) {
		return [];
	}

	const {
		tCapManage,
		tSecurityTypeTranslation,
		personsSelectOptions,
		mipSelectOptions,
		securitiesSelectOptions,
	} = context;
	const tableName = audit.tableName;

	if (tableName === 'BankAccountIdentifier') {
		return transformIdentifierAudit(audit, context);
	}

	const data: TransformedAuditData[] = [];

	const format = (value: AuditFieldValue, key: string, securityId?: string) =>
		formatValue(
			value,
			key,
			personsSelectOptions,
			mipSelectOptions,
			securitiesSelectOptions,
			securityId,
			tableName,
			tSecurityTypeTranslation
		);

	if (audit.action === AuditAction.UPDATE && audit.oldData && audit.newData) {
		const oldSecurityId = audit.oldData.securityId;
		const newSecurityId = audit.newData.securityId;

		const allKeys = new Set([...Object.keys(audit.oldData), ...Object.keys(audit.newData)]);
		allKeys.forEach((key) => {
			if (!shouldShowField(key, tableName)) {
				return;
			}

			const oldValue = audit.oldData[key];
			const newValue = audit.newData[key];

			data.push({
				id: key,
				field: formatFieldName(key, tCapManage, tableName),
				beforeValue: format(oldValue, key, oldSecurityId),
				afterValue: format(newValue, key, newSecurityId),
				isChanged: JSON.stringify(oldValue) !== JSON.stringify(newValue),
				type: 'update',
			});
		});
	} else if (audit.action === AuditAction.CREATE && audit.newData) {
		const securityId = audit.newData.securityId;

		Object.entries(audit.newData)
			.filter(([key]) => shouldShowField(key, tableName))
			.forEach(([key, value]) => {
				data.push({
					id: key,
					field: formatFieldName(key, tCapManage, tableName),
					value: format(toAuditFieldValue(value), key, securityId),
					type: 'created',
				});
			});
	} else if (audit.action === AuditAction.DELETE && audit.oldData) {
		const securityId = audit.oldData.securityId;

		Object.entries(audit.oldData)
			.filter(([key]) => shouldShowField(key, tableName))
			.forEach(([key, value]) => {
				data.push({
					id: key,
					field: formatFieldName(key, tCapManage, tableName),
					value: format(toAuditFieldValue(value), key, securityId),
					type: 'deleted',
				});
			});
	}

	return sortFieldsByOrder(data, tableName);
};
